import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FormDialog } from "@/components/ui/form-dialog";
import { FormSection } from "@/components/ui/form-section";
import { Plus, Tag } from "lucide-react";
import { IconPicker } from "./IconPicker";
import { useCreateCategory } from "@/hooks/useCategories";
import { toast } from "sonner";

interface CreateCategoryDialogProps {
  /** Controlled open state. When provided, the default trigger button is hidden. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function CreateCategoryDialog({
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}: CreateCategoryDialogProps = {}) {
  const createCategory = useCreateCategory();
  const isControlled = controlledOpen !== undefined;
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = isControlled ? controlledOpen : uncontrolledOpen;
  const setOpen = (next: boolean) =>
    isControlled ? controlledOnOpenChange?.(next) : setUncontrolledOpen(next);
  const [form, setForm] = useState({
    name: "",
    iconName: "star",
    colorHex: "1E5A9F",
    sortOrder: "99",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createCategory.mutateAsync({
        name: form.name,
        iconName: form.iconName,
        colorHex: form.colorHex.replace("#", ""), // strip # if user types it
        sortOrder: parseInt(form.sortOrder) || 99,
      });
      toast.success("Kategoria dodana");
      setOpen(false);
      setForm({ name: "", iconName: "star", colorHex: "1E5A9F", sortOrder: "99" });
    } catch (err) {
      toast.error("Nie udało się dodać kategorii", {
        description: err instanceof Error ? err.message : undefined,
      });
    }
  };

  return (
    <>
      {!isControlled && (
        <Button onClick={() => setOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Dodaj kategorię
        </Button>
      )}
      <FormDialog
        open={open}
        onOpenChange={setOpen}
        title="Nowa kategoria"
        size="sm"
        footer={
          <>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Anuluj
            </Button>
            <Button type="submit" form="category-create-form" disabled={createCategory.isPending}>
              {createCategory.isPending ? "Dodawanie..." : "Utwórz"}
            </Button>
          </>
        }
      >
        <form id="category-create-form" onSubmit={handleSubmit}>
          <FormSection icon={Tag} title="Kategoria">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nazwa</Label>
                <Input
                  id="name"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  required
                  placeholder="np. Muzyka"
                />
              </div>
              <div className="space-y-2">
                <Label>Ikona</Label>
                <IconPicker
                  value={form.iconName}
                  onChange={(iconName) => setForm((f) => ({ ...f, iconName }))}
                  colorHex={form.colorHex}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="colorHex">Kolor (hex)</Label>
                <div className="flex items-center gap-2">
                  <div
                    className="h-8 w-8 rounded border"
                    style={{ backgroundColor: `#${form.colorHex.replace("#", "")}` }}
                  />
                  <Input
                    id="colorHex"
                    value={form.colorHex}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, colorHex: e.target.value.replace("#", "") }))
                    }
                    placeholder="1E5A9F"
                    maxLength={7}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="sortOrder">Kolejność sortowania</Label>
                <Input
                  id="sortOrder"
                  type="number"
                  value={form.sortOrder}
                  onChange={(e) => setForm((f) => ({ ...f, sortOrder: e.target.value }))}
                  min="0"
                />
              </div>
            </div>
          </FormSection>
        </form>
      </FormDialog>
    </>
  );
}
